import { EstablishmentCategory } from '@/modules/accredited-establishments/api/types'

import {
  formatAddress,
  formatAddressLines,
  getEstablishmentTags
} from '@/modules/accredited-establishments/functions'
import {
  EstablishmentsListItem,
  ScrollToTopMessage
} from '@/modules/accredited-establishments/components'
import {
  useDispatchPageEvent,
  useIntersectionObserver,
  useMediaQuery
} from '@/global/hooks'
import { Skeleton } from '@/global/components/Skeleton'
import { useList } from '@/modules/accredited-establishments/hooks'
import { EventName } from '@/global/utils/analytics-events/types/common'
import {
  NotFoundMessage,
  ApiErrorMessage
} from '@/modules/accredited-establishments/components/List'
import { getValidEstablishmentImage } from '@/modules/accredited-establishments/functions/getValidEstablishmentImage'
import { ITEMS_PER_PAGE } from '@/modules/accredited-establishments/constants'

// TODO: replace by view id
const ACCREDITED_NETWORK_VIEW_ID = 'accredited-network-view'

/* Uma pagina cheia, como o handoff do estado de carga. */
const SKELETON_COUNT = ITEMS_PER_PAGE

interface AccreditedNetworkListProps {
  onEstablishmentClick?: ({
    referencedId,
    category
  }: {
    referencedId: string
    category: EstablishmentCategory
  }) => void
}

const AccreditedNetworkList = ({
  onEstablishmentClick
}: AccreditedNetworkListProps) => {
  const isMobile = useMediaQuery({ maxWidth: 'tablet' })

  useDispatchPageEvent({
    eventName: EventName.PAGE_VIEWED,
    pageTitle: 'Lista da Rede Credenciada'
  })

  const {
    establishments,
    isLoading,
    isFetchingNextPage,
    showReachEndMessage,
    fetchNextPage,
    hasNextPage,
    specialty
  } = useList()

  const { observeElementRef } = useIntersectionObserver<HTMLButtonElement>({
    callback: isIntersecting => {
      if (isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage()
    }
  })

  return (
    <>
      <div id={ACCREDITED_NETWORK_VIEW_ID}>
        <div className='w-full h-full grid grid-cols-1 lg:grid-cols-3 gap-gl-spacing-02'>
          {establishments.map(establishment => (
            <EstablishmentsListItem
              ref={observeElementRef}
              key={establishment.referencedId}
              referencedId={establishment.referencedId}
              title={establishment.name}
              address={formatAddressLines(establishment.address)}
              /*
               * O deep link precisa do endereco em uma linha, enquanto o
               * cartao segue exibindo a forma de duas linhas acima.
               */
              destination={{
                address: formatAddress(establishment.address),
                lat: establishment.lat,
                lon: establishment.lon
              }}
              image={getValidEstablishmentImage(establishment)}
              tags={getEstablishmentTags(establishment, specialty)}
              onClick={() =>
                onEstablishmentClick?.({
                  referencedId: establishment.referencedId,
                  category: establishment.category
                })
              }
            />
          ))}

          {isLoading || isFetchingNextPage
            ? Array.from({ length: SKELETON_COUNT }, (_, k) => k).map(index => (
                <Skeleton
                  key={index}
                  /*
                   * O `Skeleton` global nao tem variante para o gradiente do
                   * handoff, mas aceita `className`, entao nao precisa de
                   * componente novo.
                   *
                   * As duas paradas do gradiente ficam literais de proposito: a
                   * escala `gl-color-transparent-black-*` comeca em 10% e o
                   * handoff pede 2% e 5%. Sao valores fora da escala do design
                   * system, nao tokens que faltaram.
                   */
                  className='rounded-gl-border-radius-lg bg-gradient-to-l from-[rgba(20,20,20,0.02)] to-[rgba(20,20,20,0.05)]'
                  bgColor=''
                  height='h-[148px]'
                  width='w-full'
                />
              ))
            : null}
        </div>

        {showReachEndMessage ? (
          <ScrollToTopMessage
            constrainWidth={isMobile}
            showScrollButton={!isMobile}
          />
        ) : null}
      </div>

      <NotFoundMessage />

      <ApiErrorMessage />
    </>
  )
}

export { AccreditedNetworkList }
